/**
 * "What will I pay this month?" — the single card patients open the
 * dashboard for. Pulls live Stripe + wallet state from /api/billing/preview
 * and presents three layers in one glance:
 *
 *   1. HERO  — the soonest upcoming charge ($X on <date>) or a calm empty
 *      state when there's nothing scheduled.
 *   2. RUNWAY — every active/paused/cancel-pending subscription, with the
 *      inline actions (Pause / Resume / Cancel-undo) wired straight into
 *      /api/subscriptions/{id}/...
 *   3. SAVINGS — the patient's wallet (FL credit, best non-stackable coupon,
 *      stackable weigh-in rewards). We're honest that these apply to the
 *      next NEW order, not auto-renewals.
 *
 * Designed to sit at the top of the patient dashboard above the existing
 * SubscriptionsCard (which we keep for the deeper change-plan flow).
 */
import { useCallback, useEffect, useState } from "react";
import API from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import {
  Wallet, CalendarClock, Sparkles, PauseCircle, PlayCircle, XCircle,
  RotateCcw, Loader2, BadgeCheck, Tag, AlertCircle, Send, Download,
  ShieldCheck, Copy, Check,
} from "lucide-react";
import { toast } from "sonner";
import { openBenefitsShareModal } from "@/components/BenefitsShareModal";

function fmtMoney(n, currency = "usd") {
  const v = Number(n || 0);
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency", currency: (currency || "usd").toUpperCase(),
      minimumFractionDigits: v % 1 === 0 ? 0 : 2,
    }).format(v);
  } catch {
    return `$${v.toFixed(2)}`;
  }
}

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      weekday: "short", month: "short", day: "numeric", year: "numeric",
    });
  } catch { return iso; }
}

function daysUntil(iso) {
  if (!iso) return null;
  try {
    const ms = new Date(iso).getTime() - Date.now();
    return Math.max(0, Math.round(ms / 86400000));
  } catch { return null; }
}

function statusPill(sub) {
  if (sub.status === "canceled") return { label: "Canceled", cls: "bg-slate-200 text-slate-700" };
  if (sub.cancel_at_period_end) return { label: "Cancels at period end", cls: "bg-amber-100 text-amber-800" };
  if (sub.paused) return { label: `Paused until ${fmtDate(sub.pause_resumes_at)}`, cls: "bg-blue-100 text-blue-800" };
  if (sub.status === "past_due") return { label: "Payment past due", cls: "bg-red-100 text-red-800" };
  if (sub.status === "active" || sub.status === "trialing") return { label: "Active", cls: "bg-emerald-100 text-emerald-800" };
  return { label: sub.status || "—", cls: "bg-slate-100 text-slate-700" };
}

export default function BillingPreviewCard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [verifyLinkOpen, setVerifyLinkOpen] = useState(false);
  const [verifyLink, setVerifyLink] = useState(null); // {verify_url, expires_at, ttl_days, qr_data_url}
  const [verifyLoading, setVerifyLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const { data } = await API.get("/billing/preview");
      setData(data);
    } catch (e) {
      // Silent fail — widget self-hides on hard error so the dashboard
      // never crashes if Stripe is down.
      console.warn("billing-preview load failed", e);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const subAction = async (subId, path, successMsg) => {
    setBusyId(subId);
    try {
      await API.post(`/subscriptions/${subId}/${path}`, {});
      toast.success(successMsg);
      await refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't update subscription");
    } finally {
      setBusyId(null);
    }
  };

  if (loading) {
    return (
      <Card className="border-purple-200 dark:border-purple-900/40" data-testid="billing-preview-card-loading">
        <CardContent className="p-5 flex items-center gap-2 text-sm text-slate-500">
          <Loader2 className="w-4 h-4 animate-spin" /> Calculating your next charge…
        </CardContent>
      </Card>
    );
  }

  if (!data) return null;

  // Self-hide when the patient has no subscriptions AND no wallet credits.
  const wallet = data.wallet || {};
  const hasWallet = (wallet.total_discount || 0) > 0;
  const hasSubs = Array.isArray(data.subscriptions) && data.subscriptions.length > 0;
  if (!hasSubs && !hasWallet) return null;

  const nc = data.next_charge;
  const dLeft = nc ? daysUntil(nc.date) : null;

  return (
    <Card className="border-purple-200 dark:border-purple-900/40 overflow-hidden" data-testid="billing-preview-card">
      <CardContent className="p-0">
        {/* HERO — the question patients actually want answered */}
        <div className="bg-gradient-to-br from-purple-600 via-violet-600 to-fuchsia-600 text-white p-5 sm:p-6">
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-purple-100 mb-2">
            <Wallet className="w-3.5 h-3.5" /> Billing preview · {data.month_label}
          </div>
          {nc ? (
            <>
              <div className="flex items-end gap-3 flex-wrap">
                <div>
                  <p className="text-[11px] uppercase tracking-wider text-purple-200">Next charge</p>
                  <p className="text-4xl sm:text-5xl font-extrabold leading-none" style={{ fontFamily: "'Outfit', sans-serif" }} data-testid="billing-next-charge-amount">
                    {fmtMoney(nc.amount, nc.currency)}
                  </p>
                </div>
                <div className="text-sm text-purple-100 pb-1">
                  on <span className="font-semibold text-white" data-testid="billing-next-charge-date">{fmtDate(nc.date)}</span>
                  {dLeft !== null && (
                    <span className="ml-2 inline-flex items-center gap-1 rounded-full bg-white/15 px-2 py-0.5 text-xs">
                      <CalendarClock className="w-3 h-3" /> in {dLeft} day{dLeft === 1 ? "" : "s"}
                    </span>
                  )}
                </div>
              </div>
              <p className="text-xs text-purple-100 mt-2">
                {nc.product_name}
                {nc.cancel_at_period_end && (
                  <span className="ml-2 inline-flex items-center gap-1 rounded-full bg-amber-200/30 text-amber-50 px-2 py-0.5 text-[11px] font-semibold">
                    <AlertCircle className="w-3 h-3" /> cancels after this charge
                  </span>
                )}
              </p>
              {data.month_total_due > 0 && Math.abs(data.month_total_due - nc.amount) > 0.005 && (
                <p className="text-xs text-purple-100 mt-3 border-t border-white/15 pt-2">
                  Total renewing this month: <span className="font-semibold text-white">{fmtMoney(data.month_total_due, nc.currency)}</span>
                </p>
              )}
            </>
          ) : (
            <div data-testid="billing-no-next-charge">
              <p className="text-2xl font-bold leading-none" style={{ fontFamily: "'Outfit', sans-serif" }}>
                Nothing scheduled
              </p>
              <p className="text-sm text-purple-100 mt-2">
                {hasSubs
                  ? "All your subscriptions are paused or cancelled. Resume below to restart shipments."
                  : "You don't have any active subscriptions right now."}
              </p>
            </div>
          )}
        </div>

        {/* RUNWAY — every subscription, inline actions */}
        {hasSubs && (
          <div className="px-5 sm:px-6 py-4 border-b border-purple-100 dark:border-purple-900/40">
            <div className="space-y-2.5">
              {data.subscriptions.map((s) => {
                const pill = statusPill(s);
                const isBusy = busyId === s.id;
                const showResume = s.cancel_at_period_end && s.status !== "canceled";
                const showUnpause = s.paused;
                const showPause = !s.paused && !s.cancel_at_period_end && s.status === "active";
                const showCancelToggle = !s.cancel_at_period_end && s.status === "active";
                return (
                  <div key={s.id} className="flex flex-wrap items-center gap-3 bg-slate-50 dark:bg-slate-900 rounded-lg px-3 py-2.5" data-testid={`billing-sub-row-${s.id}`}>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-slate-900 dark:text-white truncate">{s.product_name || "GLP-1 subscription"}</p>
                      <p className="text-[11px] text-slate-500 dark:text-slate-400 flex items-center gap-2 mt-0.5">
                        <span>{fmtMoney(s.amount, s.currency)} {s.interval_count > 1 ? `every ${s.interval_count} ${s.interval}s` : `per ${s.interval}`}</span>
                        {s.next_renewal_at && <span>· renews {fmtDate(s.next_renewal_at)}</span>}
                      </p>
                    </div>
                    <span className={`text-[11px] font-semibold rounded-full px-2.5 py-0.5 ${pill.cls}`}>{pill.label}</span>
                    <div className="flex items-center gap-1">
                      {showPause && (
                        <Button size="sm" variant="ghost" disabled={isBusy} onClick={() => subAction(s.id, "pause", "Subscription paused for 30 days")} className="h-7 px-2 text-[11px]" data-testid={`billing-pause-${s.id}`}>
                          {isBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : <><PauseCircle className="w-3 h-3 mr-1" />Pause 30d</>}
                        </Button>
                      )}
                      {showUnpause && (
                        <Button size="sm" variant="ghost" disabled={isBusy} onClick={() => subAction(s.id, "unpause", "Subscription resumed")} className="h-7 px-2 text-[11px]" data-testid={`billing-unpause-${s.id}`}>
                          {isBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : <><PlayCircle className="w-3 h-3 mr-1" />Resume</>}
                        </Button>
                      )}
                      {showResume && (
                        <Button size="sm" variant="ghost" disabled={isBusy} onClick={() => subAction(s.id, "resume", "Cancellation reverted")} className="h-7 px-2 text-[11px]" data-testid={`billing-undo-cancel-${s.id}`}>
                          {isBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : <><RotateCcw className="w-3 h-3 mr-1" />Undo cancel</>}
                        </Button>
                      )}
                      {showCancelToggle && (
                        <Button size="sm" variant="ghost" disabled={isBusy} onClick={() => subAction(s.id, "cancel", "Cancellation scheduled for period end")} className="h-7 px-2 text-[11px] text-red-600 hover:text-red-700 hover:bg-red-50" data-testid={`billing-cancel-${s.id}`}>
                          <XCircle className="w-3 h-3 mr-1" />Cancel
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* SAVINGS — wallet preview */}
        <div className="px-5 sm:px-6 py-4">
          <div className="flex items-center gap-2 mb-3">
            <Sparkles className="w-4 h-4 text-amber-500" />
            <h4 className="text-sm font-bold text-slate-900 dark:text-white" style={{ fontFamily: "'Outfit', sans-serif" }}>
              Savings ready to apply
            </h4>
          </div>
          {hasWallet ? (
            <div className="space-y-2">
              {wallet.coupon != null && wallet.coupon > 0 && (
                <WalletRow icon={<Tag className="w-3.5 h-3.5" />} label={wallet.coupon_label || "Coupon (best available)"} value={fmtMoney(wallet.coupon)} testid="wallet-base-coupon" />
              )}
              {(wallet.stackable_coupons || []).map((sc) => (
                <WalletRow
                  key={sc.id}
                  icon={<Sparkles className="w-3.5 h-3.5" />}
                  label={sc.label || "Stackable reward"}
                  value={fmtMoney(sc.amount)}
                  hint={sc.code}
                  testid={`wallet-stackable-${sc.id}`}
                />
              ))}
              <div className="flex items-center justify-between pt-2 border-t border-dashed border-slate-200 dark:border-slate-800 mt-2">
                <span className="text-sm font-bold text-slate-900 dark:text-white">Total ready</span>
                <span className="text-sm font-extrabold text-emerald-600" data-testid="wallet-total">
                  {fmtMoney(wallet.total_discount)} off
                </span>
              </div>
              <p className="text-[11px] text-slate-400 italic mt-2 leading-relaxed">{data.savings_note}</p>
            </div>
          ) : (
            <p className="text-xs text-slate-400 italic">
              No active coupons or credits yet. Weigh in weekly to earn stackable rewards.
            </p>
          )}

          {/* HSA/FSA action footer — visible whenever the card itself is visible */}
          <div className="mt-4 pt-3 border-t border-slate-100 dark:border-slate-800 flex flex-wrap items-center gap-2">
            <Button
              type="button"
              onClick={() => openBenefitsShareModal()}
              size="sm"
              className="rounded-full bg-slate-900 hover:bg-slate-800 text-white text-xs h-8"
              data-testid="benefits-share-btn"
            >
              <Send className="w-3.5 h-3.5 mr-1.5" /> Send to my benefits administrator
            </Button>
            <button
              type="button"
              onClick={async () => {
                try {
                  const res = await API.get("/billing/benefits-summary.pdf", { responseType: "blob" });
                  const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
                  window.open(url, "_blank", "noopener,noreferrer");
                  setTimeout(() => URL.revokeObjectURL(url), 30000);
                } catch (e) {
                  toast.error(e.response?.data?.detail || "Couldn't generate the PDF");
                }
              }}
              className="text-xs text-slate-500 hover:text-purple-600 inline-flex items-center gap-1 ml-1"
              data-testid="benefits-download-link"
            >
              <Download className="w-3.5 h-3.5" /> or download the PDF
            </button>
            <button
              type="button"
              onClick={async () => {
                setVerifyLinkOpen(true);
                if (verifyLink) return; // reuse previously-fetched link inside the same session
                setVerifyLoading(true);
                try {
                  const { data: res } = await API.post("/billing/verify-link", {});
                  setVerifyLink(res);
                } catch (err) {
                  toast.error(err.response?.data?.detail || "Couldn't generate a fresh link");
                  setVerifyLinkOpen(false);
                } finally {
                  setVerifyLoading(false);
                }
              }}
              className="text-xs text-slate-500 hover:text-purple-600 inline-flex items-center gap-1 ml-3"
              data-testid="benefits-verify-link-btn"
            >
              <ShieldCheck className="w-3.5 h-3.5" /> Generate a fresh verify link
            </button>
          </div>
        </div>
      </CardContent>

      {/* Share modal lifted to dashboard-level <BenefitsShareModal />; this card now
          dispatches openBenefitsShareModal() from the action button instead. */}

      {/* Verify link modal — fresh-token + copy + QR for in-person CPA use */}
      <Dialog
        open={verifyLinkOpen}
        onOpenChange={(o) => {
          setVerifyLinkOpen(o);
          if (!o) setCopied(false);
        }}
      >
        <DialogContent className="sm:max-w-md" data-testid="verify-link-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldCheck className="w-4 h-4 text-emerald-600" /> Verification link
            </DialogTitle>
            <DialogDescription className="text-xs text-slate-500 leading-relaxed">
              Share this signed link directly with your HSA / FSA administrator or a CPA. They&apos;ll see only
              the minimum info needed to approve a claim (initials, masked account, 6-month totals). Valid
              for 90 days; no login required.
            </DialogDescription>
          </DialogHeader>

          {verifyLoading || !verifyLink ? (
            <div className="flex items-center gap-2 text-sm text-slate-500 py-4">
              <Loader2 className="w-4 h-4 animate-spin" /> Generating fresh link…
            </div>
          ) : (
            <div className="space-y-4">
              {/* QR */}
              <div className="flex justify-center" data-testid="verify-link-qr-wrap">
                {verifyLink.qr_data_url && (
                  <img
                    src={verifyLink.qr_data_url}
                    alt="Verification link QR code"
                    className="w-44 h-44 rounded-lg border border-slate-200 bg-white p-2"
                    data-testid="verify-link-qr"
                  />
                )}
              </div>

              {/* URL + copy */}
              <div>
                <label className="block text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1">
                  Verify URL
                </label>
                <div className="flex items-stretch gap-2">
                  <input
                    readOnly
                    value={verifyLink.verify_url || ""}
                    onClick={(e) => e.target.select()}
                    className="flex-1 text-xs font-mono rounded-lg border border-slate-200 px-3 py-2 bg-slate-50 truncate"
                    data-testid="verify-link-url"
                  />
                  <Button
                    type="button"
                    size="sm"
                    onClick={async () => {
                      try {
                        await navigator.clipboard.writeText(verifyLink.verify_url);
                        setCopied(true);
                        toast.success("Link copied");
                        setTimeout(() => setCopied(false), 2500);
                      } catch {
                        toast.error("Couldn't access clipboard — select & copy manually");
                      }
                    }}
                    className={`rounded-lg text-xs ${copied ? "bg-emerald-600 hover:bg-emerald-700" : "bg-slate-900 hover:bg-slate-800"} text-white`}
                    data-testid="verify-link-copy-btn"
                  >
                    {copied ? <><Check className="w-3.5 h-3.5 mr-1" />Copied</> : <><Copy className="w-3.5 h-3.5 mr-1" />Copy</>}
                  </Button>
                </div>
              </div>

              <p className="text-[11px] text-slate-400 leading-relaxed" data-testid="verify-link-expiry">
                Expires {verifyLink.expires_at ? new Date(verifyLink.expires_at).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : `in ${verifyLink.ttl_days} days`}
                · For in-person use, ask the verifier to scan the QR with their phone camera.
              </p>
            </div>
          )}

          <DialogFooter className="gap-2">
            <Button
              type="button"
              size="sm"
              variant="ghost"
              disabled={verifyLoading}
              onClick={async () => {
                // Force-mint a brand-new token (e.g. patient suspects old link leaked)
                setVerifyLoading(true);
                setVerifyLink(null);
                try {
                  const { data: res } = await API.post("/billing/verify-link", {});
                  setVerifyLink(res);
                  toast.success("Fresh link generated");
                } catch (err) {
                  toast.error(err.response?.data?.detail || "Couldn't refresh the link");
                } finally {
                  setVerifyLoading(false);
                }
              }}
              className="text-xs"
              data-testid="verify-link-refresh-btn"
            >
              <RotateCcw className="w-3.5 h-3.5 mr-1" /> Generate new
            </Button>
            <Button type="button" size="sm" onClick={() => setVerifyLinkOpen(false)} className="rounded-full bg-purple-600 hover:bg-purple-700 text-white text-xs" data-testid="verify-link-close-btn">
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

function WalletRow({ icon, label, value, hint, testid }) {
  return (
    <div className="flex items-center gap-2 text-xs" data-testid={testid}>
      <span className="w-6 h-6 rounded-md bg-amber-50 dark:bg-amber-900/20 flex items-center justify-center text-amber-600">
        {icon}
      </span>
      <span className="text-slate-700 dark:text-slate-200 flex-1 truncate">
        {label}
        {hint && <span className="ml-1.5 font-mono text-[10px] text-slate-400">{hint}</span>}
      </span>
      <span className="font-semibold text-slate-900 dark:text-white">{value}</span>
    </div>
  );
}
